import {useVorderState} from "@/modules/shop/store/vorder-state";
import {defineStore, storeToRefs} from "pinia";
import {computed} from "vue";
import {useCatalogStore} from "@/modules/shop/store/catalog";
import {Coupon} from "@/gql/gen";

const STORE_NAME = 'vorder-coupon'

/**
 * Composable для управления выбором купона в заказе
 *
 * Логика работы:
 * 1. Когда сервер возвращает купон, устанавливаем COUPON_ID
 * 2. Купон может быть удален пользователем
 * 3. COUPON_ID синхронизируется с vorderState.coupon
 */
export const useVorderCoupon = defineStore(STORE_NAME, () => {

    const vorderState = useVorderState();
    const catalogStore = useCatalogStore()

    const {coupon, attrs} = storeToRefs(vorderState);

    /**
     * Проверяет доступен ли купон с указанным ID
     */
    const isCouponAvailable = (couponId: number | null | undefined): boolean => {
        if (!couponId || couponId <= 0) return false;
        return coupon.value?.ID === couponId;
    };

    /**
     * Текущий выбранный купон (если доступен)
     */
    const selectedCoupon = computed(() => {
        const couponId = attrs.value.COUPON_ID;

        // Если купон выбран и доступен
        if (couponId && couponId > 0 && isCouponAvailable(couponId)) {
            return coupon.value;
        }

        return null;
    });

    /**
     * Доступен ли купон для использования
     */
    const hasCoupon = computed(() => !!coupon.value);

    /**
     * Активирован ли купон в текущем заказе
     */
    const isCouponActive = computed(() => {
        const couponId = attrs.value.COUPON_ID;
        return !!couponId && couponId > 0 && isCouponAvailable(couponId);
    });

    /**
     * Устанавливает купон для использования в заказе
     */
    const activateCoupon = () => {
        if (!coupon.value) return;
        vorderState.setAttr('COUPON_ID', coupon.value.ID);
    };

    /**
     * Удаляет купон из заказа (только локально, без вызова API)
     */
    const removeCouponLocal = () => {
        vorderState.setAttr('COUPON_ID', null);
        coupon.value = undefined;
    };

    /**
     * Автоматическая синхронизация COUPON_ID при изменении купона
     */
    const syncCouponId = () => {
        const currentCouponId = attrs.value.COUPON_ID;
        const availableCoupon = coupon.value;
        // Если есть доступный купон
        if (availableCoupon) {
            // Если COUPON_ID не установлен или установлен неверно
            if (!currentCouponId || currentCouponId <= 0 || !isCouponAvailable(currentCouponId)) {
                // Устанавливаем ID доступного купона
                vorderState.setAttr('COUPON_ID', availableCoupon.ID);
            }
        } else {
            // Если купона нет, сбрасываем COUPON_ID
            if (currentCouponId && currentCouponId > 0) {
                vorderState.setAttr('COUPON_ID', null);
            }
        }
    };

    const couponProduct = computed(() => {
        if (!coupon.value?.PRODUCT_ID) return null
        return catalogStore.productsById[coupon.value.PRODUCT_ID] || null
    })

    const selectCoupon = (couponEntity?: Coupon | null) => {
        couponEntity = couponEntity || coupon.value
        if (couponEntity) {
            vorderState.setAttr('COUPON_ID', couponEntity.ID);
        }
    }

    return {
        // State
        selectedCoupon,
        hasCoupon,
        isCouponActive,
        couponProduct,
        coupon,

        // Actions
        selectCoupon,
        activateCoupon,
        removeCouponLocal,
        syncCouponId,
        isCouponAvailable,
    };
})
